import {
  ReactElement,
  createContext,
  useContext,
  useState,
  useEffect,
  useReducer,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { v4 as uuidv4 } from 'uuid';
import { UploadedFile } from '@la/ds-ui-components';
import {
  BFFMember,
  FormField,
  FormFieldResponse,
  BFFGroupAccount,
  GroupAccountUser,
  ProgramRegistrantRegistrationOption,
  Registration,
  RegistrationDecodedParams,
  RegistrationWorkflowWaiver,
  RegistrationStatus,
  Team,
  RegistrationType,
  ProgramRole,
  WaiversAcceptancePayload,
  WorkflowRegistrationResponse,
  WaiverType,
  OGProgramRole,
  CustomGender,
  CartItemPayload,
  Product,
  AddOnOptionSelections,
} from '@la/types';
import { PlayerRoles } from '@la/types';
import {
  formatFormFieldsForFacade,
  formatFormFieldsForWorkflow,
  getProperty,
  isPlayerRegistration,
  isStaffRegistration,
} from '@la/utilities';
import { updateFormField } from 'lib/apis/getFormFields';
import { getStaffRoles } from 'lib/apis/getStaffRoles';
import {
  getUserRegistrations,
  getExistingUserRegistration,
} from 'lib/apis/getUserRegistrations';
import { updateWorkflowRegistration } from 'lib/apis/updateWorkflowRegistration';
import { uploadFile as uploadFileApi } from 'lib/apis/uploadFile';
import { DecodedTokenData } from 'lib/apis/verifyUrlSignature';
import { getLAHostnameParts } from 'lib/utils/urlUtils';
import { useRegistrationAddOns } from 'redux/hooks/useRegistrationAddOns';
import { resetRegistrationAddOns } from 'redux/registrationAddOnsSlice';
import {
  useAcceptWaiversMutation,
  useFinalizeRegistrationsMutation,
  useGetExposeNGBMembershipFieldsQuery,
  useGetMemberRegistrationPriceQuery,
  useGetRegistrantInfoQuery,
} from 'redux/services/registrationApi';
import { useGetSiteSettingsQuery } from 'redux/services/siteInfo';
import { useUpdateFormFieldsValuesMutation } from 'redux/services/tournamentApi';
import { useAppDispatch } from 'redux/store';
import { WaiverData } from 'domains/ConsolidatedCheckout/Checkout.types';
import { getDecodedData } from './RegistrationContext.utils';
import {
  RegistrantState,
  RegistrantStateActions,
  registrantInitialState,
  registrantReducer,
} from './reducer';
import { useRegistrantSteps } from './steps';

type Program = {
  id?: number;
  masterProgramId: number;
  name: string;
  details: {
    hotelLinks?: string;
  };
};

export type RegistrantInfoPayload = {
  decodedData?: DecodedTokenData;
  formFields: FormFieldResponse;
  groupAccount: BFFGroupAccount | null;
  loggedInUserId: number;
  masterProgram: Program;
  memberFormFields: FormField[];
  program: Program;
  structuredStateEnabled: boolean;
  team: Team | null;
  waivers: WaiverData[];
};

export type FormFieldUpdateData = {
  customFields: FormField[];
  registrationId?: string;
  userId?: string;
};

export type FileUploadData = {
  file: UploadedFile;
  propertyDefinitionId: string;
  userId: string;
};

type MemberProfileSettings = {
  childBirthDateRequired: boolean;
  childEmailEnabled: boolean;
};

export type RegistrationContextProps = RegistrantState & {
  ctx: ReturnType<typeof getRegistrantContext>;
  currentRegistration: WorkflowRegistrationResponse | null;
  decodedData: RegistrationDecodedParams | null;
  existingRegistration: Registration | null;
  feeValue: string | undefined;
  formFields: FormFieldResponse;
  genders: CustomGender[];
  groupAccount: BFFGroupAccount | null;
  hasErrors: boolean;
  hasRequestLoading?: boolean;
  hideGender?: boolean;
  hotelLink?: string;
  loading: boolean;
  loggedInUserId: number;
  loggedInUserName: string;
  masterProgramId?: number;
  masterProgramLocationName?: string;
  masterProgramName?: string;
  memberFormFields: FormField[];
  memberProfileSettings: MemberProfileSettings;
  ngbMembershipType?: string | null;
  registrationId?: string;
  registrationOptions: ProgramRegistrantRegistrationOption[] | null;
  roleName?: string;
  setHasErrors: React.Dispatch<React.SetStateAction<boolean>>;
  setTouched: React.Dispatch<React.SetStateAction<boolean>>;
  setWaiversTouched: React.Dispatch<React.SetStateAction<boolean>>;
  structuredStateEnabled: boolean;
  subdomain: string;
  teamName: string;
  touched: boolean;
  tournamentId: string;
  userRegistrations?: Registration[];
  waivers: WaiverData[] | null;
  waiversTouched: boolean;
  dispatch: React.Dispatch<RegistrantStateActions>;
  updateFormFields: (data: FormFieldUpdateData) => Promise<{}[]>;
  updateWithExistingRegistration: (userId: string) => Promise<void>;
  uploadFile: (data: FileUploadData) => Promise<{ uuid: string }>;
  setCurrentRegistration: (
    registrationId: WorkflowRegistrationResponse
  ) => void;
  setHasRequestLoading: (hasRequestLoading: boolean) => void;
  setRegistrationId: (registrationId: string) => void;
  setRegistrationOptions: (
    options: ProgramRegistrantRegistrationOption[]
  ) => void;
};

export const getRegistrantContext = (
  existingRegistration: Registration | null | undefined,
  decodedData: RegistrationDecodedParams | undefined
) => {
  if (!existingRegistration && !decodedData) {
    return null;
  }

  if (existingRegistration) {
    const { properties } = existingRegistration;
    let programRole;
    if (isPlayerRegistration(existingRegistration.registrationType)) {
      programRole =
        getProperty('programRole', properties)?.[0] ??
        // DADS NG->OG mapping: programRole -> base_event_role and original_role
        getProperty('base_event_role', properties)?.[0] ??
        getProperty('original_role', properties)?.[0];
      if (programRole === OGProgramRole.FreeAgent) {
        programRole = ProgramRole.FreeAgent;
      }
    } else {
      programRole =
        getProperty('programStaffId', properties)?.[0] ??
        getProperty('program_staff_id', properties)?.[0];
    }
    return {
      siteId: existingRegistration.siteId,
      programId: existingRegistration.programId,
      teamId: existingRegistration.teamIdOg,
      roleId: programRole,
      type: existingRegistration.registrationType as RegistrationType,
    };
  }

  if (decodedData) {
    return {
      siteId: parseInt(decodedData.site ?? ''),
      programId: decodedData.prid,
      teamId: decodedData.team,
      roleId: decodedData.role,
      type: decodedData.type as RegistrationType,
    };
  }
};

const initialValues: RegistrationContextProps = {
  ...registrantInitialState,
  ctx: null,
  currentRegistration: null,
  decodedData: null,
  existingRegistration: null,
  feeValue: undefined,
  formFields: null,
  genders: [],
  groupAccount: null,
  hasErrors: false,
  hasRequestLoading: false,
  hideGender: false,
  loading: true,
  loggedInUserId: 0,
  loggedInUserName: '',
  masterProgramId: undefined,
  masterProgramName: '',
  memberFormFields: [],
  memberProfileSettings: {
    childBirthDateRequired: false,
    childEmailEnabled: false,
  },
  ngbMembershipType: undefined,
  registrationId: undefined,
  registrationOptions: null,
  structuredStateEnabled: true,
  subdomain: '',
  teamName: '',
  touched: false,
  tournamentId: '',
  waivers: null,
  waiversTouched: false,
  dispatch: () => {},
  setCurrentRegistration: () => {},
  setHasErrors: () => {},
  setHasRequestLoading: () => {},
  setRegistrationId: () => {},
  setRegistrationOptions: () => {},
  setTouched: () => {},
  setWaiversTouched: () => {},
  updateFormFields: async () => [],
  updateWithExistingRegistration: async () => {},
  uploadFile: async () => ({ uuid: '' }),
};

export const getProgramRole = (type: string) => {
  if (type.toLocaleUpperCase() === RegistrationType.Player) {
    return ProgramRole.Player;
  }

  if (type.toLocaleUpperCase() === RegistrationType.PlayerFreeAgent) {
    return ProgramRole.FreeAgent;
  }

  return undefined;
};

export const programRoleToPlayerRole = (
  programRole?: ProgramRole
): PlayerRoles => {
  if (programRole === ProgramRole.FreeAgent) {
    return 'freeAgent';
  }

  return 'player';
};

const getContextRegistrationType = (
  type: RegistrationType | null | undefined
): 'player' | 'staff' => {
  return isPlayerRegistration(type) ? 'player' : 'staff';
};

const addRequiredAddOnsToUserSelections = (
  requiredAddOns: Product[],
  setUserSelections: (selections: AddOnOptionSelections[]) => void
) => {
  const initialUserSelections: AddOnOptionSelections[] = requiredAddOns.map(
    (addOn: Product) => {
      return {
        addOnId: addOn.id,
        addOnType: 'required',
        productInstanceId: uuidv4(),
        quantity: 1,
        selections: [],
      };
    }
  );
  setUserSelections(initialUserSelections);
};

export const RegistrationContext =
  createContext<RegistrationContextProps>(initialValues);

export const RegistrationProvider = ({
  children,
}: {
  children: ReactElement;
}) => {
  const appDispatch = useAppDispatch();
  const [state, dispatch] = useReducer(
    registrantReducer,
    registrantInitialState
  );

  const [searchParams] = useSearchParams();
  const { encodedToken, decodedParams, initialRegistrationId } = getDecodedData(
    searchParams.get('selectRegistrationData'),
    searchParams.get('registrationId')
  );
  const [touched, setTouched] = useState(false);
  const [waiversTouched, setWaiversTouched] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [roleName, setRoleName] = useState<string | undefined>();
  const [registrationId, setRegistrationId] = useState<string>(
    initialRegistrationId ?? ''
  );
  const [registrationOptions, setRegistrationOptions] = useState<
    ProgramRegistrantRegistrationOption[] | null
  >(null);
  const [userRegistrations, setUserRegistrations] = useState<Registration[]>(
    []
  );
  const [existingRegistration, setExistingRegistration] =
    useState<Registration | null>(null);
  // TODO::Eduardo (RP-2690): Currently we have a few ways of determining the existing registration's id.
  // The idea is to have this state revised but for now I'm keeping this separated to avoid any unintended side effects on `registrationId`
  const [currentRegistration, setCurrentRegistration] =
    useState<WorkflowRegistrationResponse | null>(null);
  const [hasRequestLoading, setHasRequestLoading] = useState<boolean>();
  const [params] = useState(decodedParams);
  const { subdomain } = getLAHostnameParts();
  const { playersAndStaffFees, releasePayOnce } = useFlags();

  const {
    getRequiredAddOnsByType,
    setRegistrationAddOns,
    setRegistrationType,
    setTaxRate,
    setUserSelections,
  } = useRegistrationAddOns();

  const {
    data: registrantInfoData,
    isLoading: isRegistrantInfoLoading,
    error: registrationError,
  } = useGetRegistrantInfoQuery(
    {
      encodedToken: !params ? encodedToken : undefined,
      decodedParams: params,
      siteDomain: subdomain,
      registrationId: initialRegistrationId ?? null,
    },
    {
      skip: !encodedToken && !params && !initialRegistrationId,
    }
  );

  const ctx = getRegistrantContext(
    registrantInfoData?.existingRegistration,
    params ?? registrantInfoData?.decodedData
  );

  const isPlayer = isPlayerRegistration(ctx?.type);

  useEffect(() => {
    const addOnData = registrantInfoData?.addOnProducts;
    if (addOnData) {
      setRegistrationAddOns(addOnData);
    }

    const registrationType = isPlayer ? 'player' : 'staff';
    setRegistrationType(registrationType);

    const taxRate = registrantInfoData?.taxRate ?? 0;
    setTaxRate(taxRate);
  }, [
    isPlayer,
    registrantInfoData?.addOnProducts,
    registrantInfoData?.taxRate,
    setRegistrationAddOns,
    setRegistrationType,
    setTaxRate,
  ]);

  const { data: registrationPrice } = useGetMemberRegistrationPriceQuery(
    {
      masterProgramId: registrantInfoData?.masterProgram?.id?.toString() ?? '',
      userId: isPlayer
        ? parseInt(state.selectedPlayer)
        : (registrantInfoData?.loggedInUserId ?? 0),
      type: ctx?.type as RegistrationType,
      siteDomain: subdomain,
      programId: ctx?.programId.toString() ?? '',
      checkForPayOnceRegistrations: Boolean(releasePayOnce),
    },
    {
      skip:
        !playersAndStaffFees ||
        !ctx ||
        !registrantInfoData ||
        (isPlayer && !state.selectedPlayer),
    }
  );

  const { data: ngbMembershipType, isLoading: ngbMembershipTypeIsLoading } =
    useGetExposeNGBMembershipFieldsQuery(
      {
        programId: ctx?.programId.toString(),
        subdomain: subdomain,
      },
      { skip: !ctx?.programId || !subdomain }
    );

  const siteId = ctx?.siteId;
  const { data: siteSettings, isLoading: siteSettingsIsLoading } =
    useGetSiteSettingsQuery(siteId ? siteId : 0, { skip: !siteId });

  useEffect(() => {
    if (registrantInfoData?.masterProgram?.id && subdomain) {
      getUserRegistrations({
        programId: registrantInfoData?.masterProgram?.id,
        siteDomain: subdomain,
      }).then((response) => {
        setUserRegistrations(response.userRegistrations);
      });
    }
  }, [registrantInfoData, subdomain, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'SET_GROUP_ACCOUNT',
      payload: registrantInfoData?.groupAccount ?? null,
    });
  }, [registrantInfoData]);
  useEffect(() => {
    if (ctx && ctx.siteId && ctx.roleId && !isNaN(parseInt(ctx.roleId))) {
      getStaffRoles({ siteId: ctx.siteId.toString() })
        .then((staffRoles) => {
          const programStaffRole = staffRoles.find((staffRole) => {
            if (!ctx.roleId) {
              return false;
            }
            return staffRole.id === parseInt(ctx.roleId);
          })?.role;
          setRoleName(programStaffRole);
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }, [ctx]);

  useEffect(() => {
    if (registrantInfoData?.existingRegistration) {
      const { existingRegistration } = registrantInfoData;
      const { registrationType, waivers } = existingRegistration;

      // Check for already registered status
      if (
        existingRegistration.registrationStatus ===
        RegistrationStatus.Registered
      ) {
        const message = isPlayerRegistration(
          existingRegistration.registrationType
        )
          ? 'This player already has a completed registration. Please select a different player.'
          : 'You have already registered for this tournament.';
        dispatch({
          type: 'SET_EXISTING_REGISTRATION_ERROR',
          payload: message,
        });

        return;
      }
      dispatch({ type: 'SET_EXISTING_REGISTRATION_ERROR', payload: '' });

      // If player registration, set selected player
      if (isPlayerRegistration(existingRegistration.registrationType)) {
        const registeredUserId =
          getProperty(
            'registeredUserId',
            existingRegistration.properties
          )?.[0] ?? existingRegistration.registeredUserId;

        dispatch({
          type: 'SET_SELECTED_PLAYER',
          payload: registeredUserId?.toString() ?? '',
        });
      }

      // Set add-on selections from existing registration
      setRegistrationAddOns(registrantInfoData.addOnProducts ?? []);
      setTaxRate(registrantInfoData.taxRate ?? 0);
      setUserSelections(registrantInfoData.userSelections ?? []);
      const contextRegistrationType =
        getContextRegistrationType(registrationType);
      setRegistrationType(contextRegistrationType);

      // Fill out form fields and waivers the registrant already has completed
      const { formFields: existingFormFields, waivers: existingWaivers } =
        existingRegistration;
      if (existingFormFields && existingFormFields.length) {
        const allFormFields: FormField[] = [
          ...(registrantInfoData.formFields?.nonFileUploadFormFields ?? []),
          ...(registrantInfoData.formFields?.fileUploadFormFields ?? []),
        ];

        existingFormFields.forEach((formField) => {
          const { formFieldId, values } = formField;
          const baseFormField = allFormFields.find(
            (field) => field.propertyDefinitionId === formFieldId
          );
          const type = baseFormField?.type;

          if (type) {
            if (type === 'FILE_UPLOAD') {
              if (baseFormField) {
                dispatch({
                  type: 'SET_FILE_UPLOAD_FORM_FIELD',
                  payload: {
                    id: formFieldId,
                    field: {
                      ...baseFormField,
                      value: {
                        file: null,
                        name: values[0],
                        uuid: values[1],
                      },
                    },
                  },
                });
              }
            } else {
              if (baseFormField) {
                let value;
                if (type === 'MULTIPLE_CHECKBOXES') {
                  const { items } = baseFormField;
                  value = values
                    .map((searchValue) => {
                      return items.find(
                        (item) =>
                          item.value === searchValue ||
                          item.name === searchValue
                      )?.itemId;
                    })
                    .filter((value) => !!value);
                } else if (type === 'PICK_LIST') {
                  const { items } = baseFormField;
                  const searchValue = values[0];
                  value = items.find(
                    (item) =>
                      item.value === searchValue || item.name === searchValue
                  )?.itemId;
                } else {
                  value = values[0];
                }

                dispatch({
                  type: 'SET_NON_FILE_UPLOAD_FORM_FIELD',
                  payload: {
                    id: formFieldId,
                    field: {
                      ...baseFormField,
                      value: value as any,
                    },
                  },
                });
              }
            }
          }
        });
      }

      if (existingWaivers && existingWaivers.length) {
        existingWaivers.forEach((waiver) => {
          if (waivers) {
            const currentWaiver = waivers.find(
              ({ waiverId }) => waiver.waiverId === waiverId
            );
            if (currentWaiver) {
              dispatch({
                type: 'SET_SIGNED_WAIVERS',
                payload: {
                  id: waiver.waiverId.toString(),
                  signed: true,
                },
              });
            }
          }
        });
      }
    } else {
      // Set default add-ons for new registrations
      const contextRegistrationType = getContextRegistrationType(
        registrantInfoData?.decodedData?.type
      );
      const requiredAddOns = contextRegistrationType
        ? getRequiredAddOnsByType(contextRegistrationType)
        : [];
      addRequiredAddOnsToUserSelections(requiredAddOns, setUserSelections);
    }
  }, [
    appDispatch,
    registrantInfoData,
    getRequiredAddOnsByType,
    setRegistrationAddOns,
    setTaxRate,
    setUserSelections,
    setRegistrationType,
  ]);

  const updateStateFromRegistration = (
    existingRegistration: Registration | undefined
  ) => {
    if (!existingRegistration || !registrantInfoData) {
      return null;
    }
    if (
      existingRegistration.registrationStatus === RegistrationStatus.Registered
    ) {
      const message = isPlayerRegistration(
        existingRegistration.registrationType
      )
        ? 'This player already has a completed registration. Please select a different player.'
        : 'You have already registered for this tournament.';
      dispatch({
        type: 'SET_EXISTING_REGISTRATION_ERROR',
        payload: message,
      });

      return;
    }
    dispatch({ type: 'SET_EXISTING_REGISTRATION_ERROR', payload: '' });

    if (isPlayerRegistration(existingRegistration.registrationType)) {
      const registeredUserId =
        getProperty('registeredUserId', existingRegistration.properties)?.[0] ??
        existingRegistration.registeredUserId;

      dispatch({
        type: 'SET_SELECTED_PLAYER',
        payload: registeredUserId?.toString() ?? '',
      });
    }

    const { formFields: existingFormFields, waivers: existingWaivers } =
      existingRegistration;
    if (existingFormFields && existingFormFields.length) {
      const allFormFields: FormField[] = [
        ...(registrantInfoData.formFields?.nonFileUploadFormFields ?? []),
        ...(registrantInfoData.formFields?.fileUploadFormFields ?? []),
      ];

      existingFormFields.forEach((formField) => {
        const { formFieldId, values } = formField;
        const baseFormField = allFormFields.find(
          (field) => field.propertyDefinitionId === formFieldId
        );
        const type = baseFormField?.type;

        if (type) {
          if (type === 'FILE_UPLOAD') {
            if (baseFormField) {
              dispatch({
                type: 'SET_FILE_UPLOAD_FORM_FIELD',
                payload: {
                  id: formFieldId,
                  field: {
                    ...baseFormField,
                    value: {
                      file: null,
                      name: values[0],
                      uuid: values[1],
                    },
                  },
                },
              });
            }
          } else {
            if (baseFormField) {
              let value;
              if (type === 'MULTIPLE_CHECKBOXES') {
                const { items } = baseFormField;
                value = values
                  .map((searchValue) => {
                    return items.find(
                      (item) =>
                        item.value === searchValue || item.name === searchValue
                    )?.itemId;
                  })
                  .filter((value) => !!value);
              } else if (type === 'PICK_LIST') {
                const { items } = baseFormField;
                const searchValue = values[0];
                value = items.find(
                  (item) =>
                    item.value === searchValue || item.name === searchValue
                )?.itemId;
              } else {
                value = values[0];
              }

              dispatch({
                type: 'SET_NON_FILE_UPLOAD_FORM_FIELD',
                payload: {
                  id: formFieldId,
                  field: {
                    ...baseFormField,
                    value: value as any,
                  },
                },
              });
            }
          }
        }
      });
    }

    if (existingWaivers && existingWaivers.length) {
      existingWaivers.forEach((waiver) => {
        if (waivers) {
          const currentWaiver = waivers.find(
            ({ waiverId }) => waiver.waiverId === waiverId
          );
          if (currentWaiver) {
            dispatch({
              type: 'SET_SIGNED_WAIVERS',
              payload: {
                id: waiver.waiverId.toString(),
                signed: true,
              },
            });
          }
        }
      });
    }
  };

  const updateWithExistingRegistration = async (userId: string) => {
    if (!ctx) {
      return;
    }

    return getExistingUserRegistration({
      registrationType: ctx.type,
      userId: parseInt(userId),
      programId: parseInt(ctx.programId.toString()),
      teamIdOg: ctx.teamId ? parseInt(ctx.teamId.toString()) : undefined,
      roleId:
        ctx.roleId && !isNaN(parseInt(ctx.roleId))
          ? parseInt(ctx.roleId)
          : undefined,
    }).then((existingRegistration) => {
      setExistingRegistration(existingRegistration ?? null);
      updateStateFromRegistration(existingRegistration);
    });
  };

  const [updated, setUpdated] = useState(false);
  useEffect(() => {
    if (
      registrantInfoData &&
      ctx &&
      isPlayerRegistration(ctx.type) &&
      !updated
    ) {
      const userId = localStorage.getItem('selectedPlayer');
      if (userId) {
        updateWithExistingRegistration(userId).catch((e) => console.error(e));
        setUpdated(true);
      }
    }
    if (
      registrantInfoData &&
      ctx &&
      isStaffRegistration(ctx.type) &&
      !updated
    ) {
      if (registrantInfoData?.loggedInUserId) {
        updateWithExistingRegistration(
          registrantInfoData.loggedInUserId.toString()
        ).catch((e) => console.error(e));
        setUpdated(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, registrantInfoData, updated]);

  if (registrationError) {
    // This error message will be treated in another ticket
    throw new Error('Unable to fetch registrant data');
  }

  if (
    !registrantInfoData ||
    isRegistrantInfoLoading ||
    siteSettingsIsLoading ||
    ngbMembershipTypeIsLoading
  ) {
    return (
      <RegistrationContext.Provider value={initialValues}>
        {!registrantInfoData ||
        siteSettingsIsLoading ||
        ngbMembershipTypeIsLoading
          ? null
          : children}
      </RegistrationContext.Provider>
    );
  }

  const {
    decodedData,
    formFields,
    loggedInUserId,
    loggedInUserName,
    masterProgram,
    memberFormFields,
    structuredStateEnabled,
    team,
    waivers,
    genders,
  } = registrantInfoData;

  const feeValue = registrationPrice?.price;

  const uploadFile = ({
    file,
    propertyDefinitionId,
    userId,
  }: FileUploadData): Promise<{ uuid: string }> => {
    return uploadFileApi({
      siteId: ctx?.siteId.toString() ?? '',
      propertyDefinitionId,
      userId,
      file: file.file as ArrayBuffer,
      filename: file.name,
    });
  };

  const updateFormFields = ({
    customFields,
    registrationId,
    userId,
  }: FormFieldUpdateData): Promise<{}[]> => {
    return Promise.all(
      customFields.map((field) => {
        const { propertyDefinitionId, type, value } = field;
        // Do not make call to update form field if there is no value
        if (!value) {
          return {};
        }

        let values;
        switch (type) {
          case 'FILE_UPLOAD':
            let uuid = value.uuid;
            if (uuid) {
              values = [
                {
                  registrationId,
                  userId,
                  value: uuid,
                },
              ];
            } else {
              // If the form is not uploaded yet (`uuid` is undefined), upload
              // the file first and then set `values` accordingly.
              if (userId) {
                return uploadFile({
                  file: { file: value.file, name: value.name },
                  propertyDefinitionId: propertyDefinitionId.toString(),
                  userId: userId,
                }).then(({ uuid }) => {
                  let id = registrationId ? { registrationId } : { userId };

                  return updateFormField({
                    siteId: ctx?.siteId.toString() ?? '',
                    formFieldId: propertyDefinitionId.toString(),
                    values: [
                      {
                        ...id,
                        value: uuid,
                      },
                    ],
                  });
                });
              }

              throw new Error('A user id must be supplied to upload a file.');
            }
            break;
          case 'MULTIPLE_CHECKBOXES':
            values = value.map((itemId) => ({
              registrationId,
              userId,
              value: itemId,
            }));
            break;
          default:
            values = [
              {
                registrationId,
                userId,
                value,
              },
            ];
            break;
        }

        return updateFormField({
          siteId: ctx?.siteId.toString() ?? '',
          formFieldId: propertyDefinitionId.toString(),
          values,
        });
      })
    );
  };

  return (
    <RegistrationContext.Provider
      value={{
        ...state,
        dispatch,
        hasRequestLoading,
        waivers,
        formFields,
        decodedData,
        ctx,
        loggedInUserId,
        loggedInUserName,
        memberFormFields,
        memberProfileSettings: {
          childBirthDateRequired: !!siteSettings?.childBirthDateRequired,
          childEmailEnabled: !!siteSettings?.childEmailEnabled,
        },
        structuredStateEnabled,
        tournamentId: ctx?.programId.toString() ?? '',
        masterProgramId: masterProgram?.id,
        masterProgramLocationName: masterProgram?.location?.name,
        masterProgramName: masterProgram?.name,
        ngbMembershipType,
        hotelLink: masterProgram?.details.hotelLinks,
        registrationId,
        teamName: team?.name ?? '',
        genders: genders ?? [],
        touched,
        setTouched,
        updateFormFields,
        uploadFile,
        hasErrors,
        setHasErrors,
        waiversTouched,
        setWaiversTouched,
        updateWithExistingRegistration,
        roleName,
        registrationOptions,
        setRegistrationId,
        setRegistrationOptions,
        subdomain,
        userRegistrations: userRegistrations ?? [],
        loading: isRegistrantInfoLoading,
        feeValue,
        hideGender: siteSettings?.hideGender,
        existingRegistration,
        currentRegistration,
        setCurrentRegistration,
        setHasRequestLoading,
      }}
    >
      {children}
    </RegistrationContext.Provider>
  );
};

export const useRegistration = () => {
  const { ngESignature } = useFlags();
  const dispatch = useAppDispatch();
  const { getSelectedProducts, getTotalProductCost } = useRegistrationAddOns();

  const context = useContext(RegistrationContext);
  const {
    ctx,
    currentRegistration,
    eSignature,
    feeValue,
    fileUploadFormFields,
    groupAccount,
    hasRequestLoading,
    loggedInUserId,
    loggedInUserName,
    masterProgramId,
    masterProgramLocationName,
    ngbMembershipType,
    nonFileUploadFormFields,
    selectedPlayer,
    signedWaivers,
    subdomain,
    userRegistrations,
    waivers,
    setCurrentRegistration,
    setHasErrors,
    setHasRequestLoading,
    setWaiversTouched,
  } = context;
  const navigate = useNavigate();
  const [isCartLoading, setIsCartLoading] = useState(false);

  const {
    ngTournamentNgbVerification,
    tournamentTeamRegistrationWizardVersion,
    playersAndStaffFees,
  } = useFlags();

  const {
    currentStep,
    formSteps,
    stepNumber,
    numberOfTotalSteps,
    getNextStep,
    getPreviousStep,
    onNextClick,
    onBackClick,
    steps,
  } = useRegistrantSteps(
    ctx?.type,
    context.formFields,
    ngTournamentNgbVerification && ngbMembershipType,
    masterProgramId?.toString() ?? ''
  );

  const [finalizeRegistrations] = useFinalizeRegistrationsMutation();
  const [acceptWaivers] = useAcceptWaiversMutation();
  const [updateFormFieldsValues] = useUpdateFormFieldsValuesMutation();
  const childPlayers: GroupAccountUser[] = (groupAccount?.members ?? [])
    .filter((member: BFFMember) => member.user.type === 'child')
    .map((member: BFFMember) => member.user);

  const adultEmails: string[] = (groupAccount?.members ?? [])
    .filter((member: BFFMember) => member.user.type === 'adult')
    .map((member: BFFMember) => member.user.email);

  const pendingInvitationPlayers: Registration[] = (
    userRegistrations || []
  ).filter((reg) => reg.registrationStatus === 'PENDING_INVITE');

  let name: string;
  if (isPlayerRegistration(ctx?.type)) {
    const player = childPlayers.find((p) => p.id.toString() === selectedPlayer);
    name = `${player?.firstName} ${player?.lastName}`;
  } else {
    name = loggedInUserName;
  }

  const programStaffId =
    ctx && isStaffRegistration(ctx.type) && ctx.roleId
      ? parseInt(ctx.roleId)
      : undefined;

  const onWaiversSubmit = async () => {
    if (!waivers) {
      return null;
    }
    setHasRequestLoading(true);
    if (!currentRegistration && ctx) {
      const response = await updateWorkflowRegistration(
        {
          name,
          programId: parseInt(ctx.programId.toString()),
          programRole: getProgramRole(ctx.type),
          programStaffId,
          registeredUserId: isPlayerRegistration(ctx.type)
            ? parseInt(selectedPlayer)
            : loggedInUserId,
          registeringUserId: loggedInUserId,
          registrationType: ctx.type,
          siteId: ctx.siteId,
          teamIdOg: ctx.teamId ? parseInt(ctx.teamId.toString()) : 0,
          workflowVersion: Math.trunc(tournamentTeamRegistrationWizardVersion),
          waivers: [],
        },
        subdomain
      );
      const registration = response.data.registration;
      if (!registration) {
        console.error('Registration not found.');
        return;
      }
      setCurrentRegistration(registration);
      await acceptFacadeWaivers(waivers, registration);
    } else {
      if (!currentRegistration) {
        return;
      }
      await acceptFacadeWaivers(waivers, currentRegistration);
    }
  };

  const acceptFacadeWaivers = async (
    waivers: WaiverData[],
    registration: WorkflowRegistrationResponse
  ) => {
    setWaiversTouched(true);
    let hasError = false;
    if (
      waivers.some((waiver) => !signedWaivers[waiver.waiverId]) ||
      (ngESignature && !eSignature?.trim())
    ) {
      hasError = true;
    }
    setHasErrors(hasError);
    if (hasError || !ctx) {
      return;
    }

    try {
      const registrationWaivers: RegistrationWorkflowWaiver[] = Object.keys(
        signedWaivers
      ).map((waiverId, index) => ({
        waiverId: parseInt(waiverId),
        waiverType: index === 0 ? WaiverType.Main : WaiverType.Custom,
        eSignature: eSignature?.trim(),
      }));

      const typeProperties = isPlayerRegistration(ctx.type)
        ? { playerRole: programRoleToPlayerRole(getProgramRole(ctx.type)) }
        : {
            programStaffId,
          };
      const waiversPayload = registrationWaivers.map<WaiversAcceptancePayload>(
        (waiver) => ({
          ...typeProperties,
          eSignature: eSignature?.trim(),
          waiverId: waiver.waiverId,
          programId: registration.ProgramId,
          externalRegistrationId: registration.Id,
          waiverType: waiver.waiverType,
        })
      );
      await acceptWaivers({ siteDomain: subdomain, waiversPayload })
        .unwrap()
        .finally(() => setHasRequestLoading(false));
      onNextClick();
    } catch (error) {
      const waiversId = waivers?.map((waiver) => waiver.waiverId).join(', ');
      console.error(`Error during waivers ${waiversId} submission:`, error);
    }
  };

  const totalProductCost = getTotalProductCost();
  const addToCart: boolean =
    totalProductCost ||
    (playersAndStaffFees && feeValue && !isNaN(+feeValue) && +feeValue > 0);

  const submitRegistration = async () => {
    if (ctx) {
      setHasRequestLoading(true);
      const formFields = [
        Object.values(nonFileUploadFormFields),
        Object.values(fileUploadFormFields),
      ].flat();

      const registrationFormFields = formatFormFieldsForWorkflow(formFields);

      const registrationWaivers: RegistrationWorkflowWaiver[] = Object.keys(
        signedWaivers
      ).map((waiverId) => ({
        waiverId: parseInt(waiverId),
        waiverType: 'REGISTRATION' as 'REGISTRATION',
      }));

      const payload = {
        finalize: !addToCart,
        formFields: registrationFormFields,
        name,
        programId: parseInt(ctx.programId.toString()),
        programRole: getProgramRole(ctx?.type),
        programStaffId:
          isStaffRegistration(ctx.type) && ctx.roleId
            ? parseInt(ctx.roleId)
            : undefined,
        registeredUserId: isPlayerRegistration(ctx.type)
          ? parseInt(selectedPlayer)
          : loggedInUserId,
        registrationType: ctx.type,
        registeringUserId: loggedInUserId,
        siteId: ctx.siteId,
        teamIdOg: ctx.teamId ? parseInt(ctx.teamId.toString()) : 0,
        waivers: registrationWaivers,
        teamName: context.teamName ? context.teamName : undefined,
        workflowVersion: Math.trunc(tournamentTeamRegistrationWizardVersion),
      };

      const { data } = await updateWorkflowRegistration(payload, subdomain);
      const workflowRegistration = data.registration;
      if (!workflowRegistration) {
        console.error('Registration not found.');
        return;
      }

      const facadedFormFields = formatFormFieldsForFacade({
        formFields,
        legacyRegistrationId: workflowRegistration.LegacyRegistrationId,
        userId: workflowRegistration.RegisteredUserId,
        currentStep,
      });

      const selectedAddOns = getSelectedProducts();
      let cartItemAddons: CartItemPayload[] = [];

      if (selectedAddOns.length) {
        cartItemAddons = selectedAddOns.map((selectedAddOn) => {
          return {
            productId: selectedAddOn.productId.toString(),
            productInstanceId: selectedAddOn.productInstanceId || '',
            programId: ctx.programId.toString(),
            properties: selectedAddOn.selectionTypes.map((selectionType) => {
              return {
                propertyDefinitionId: selectionType.optionId.toString(),
                value: selectionType.selectedValue,
              };
            }),
            quantity: selectedAddOn.quantity,
            registrationId: workflowRegistration.Id,
          };
        });
      }

      await updateFormFieldsValues({
        siteDomain: subdomain,
        payload: facadedFormFields,
      })
        .unwrap()
        .finally(() => setHasRequestLoading(false));

      if (addToCart) {
        setIsCartLoading(true);
        setHasRequestLoading(true);
        await finalizeRegistrations({
          registrations: [
            {
              ...(payload as any),
              id: workflowRegistration.Id,
              teamName: context.teamName,
            },
          ],
          addOns: cartItemAddons,
          siteDomain: subdomain,
          programId: ctx.programId.toString(),
          type: ctx.type,
          userId: isPlayerRegistration(ctx.type)
            ? parseInt(selectedPlayer)
            : loggedInUserId,
        })
          .unwrap()
          .finally(() => setHasRequestLoading(false));
        setIsCartLoading(false);
        dispatch(resetRegistrationAddOns());
        navigate('/cartSummary');
      } else {
        onNextClick();
        setHasRequestLoading(false);
      }
    }
  };

  return {
    ...context,
    addToCart,
    adultEmails,
    childPlayers,
    currentStep,
    formSteps,
    hasRequestLoading,
    isCartLoading,
    masterProgramId,
    masterProgramLocationName,
    name,
    ngbMembershipType,
    numberOfTotalSteps,
    pendingInvitationPlayers,
    showNGBVerification: !!ngTournamentNgbVerification,
    siteId: ctx?.siteId,
    stepNumber,
    steps,
    tournamentId: ctx?.programId,
    getNextStep,
    getPreviousStep,
    onNextClick,
    onBackClick,
    onWaiversSubmit,
    submitRegistration,
  };
};
